import React, { useState, useEffect, useMemo } from 'react';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import btc1HistoryService from '../../services/btc1HistoryService';
import './Btc1TransactionHistory.css';

const Btc1TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);

  const filterOptions = [
    { value: 'all', label: 'All Transaction' },
    { value: 'swap', label: 'Swap' },
    { value: 'deposit', label: 'Deposits' },
    { value: 'fiat_request', label: 'Fiat Requests' },
  ];

  useEffect(() => {
    fetchHistory();
  }, [filter, currentPage]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await btc1HistoryService.getTransactionHistory({ 
        type: filter === 'all' ? undefined : filter,
        limit: itemsPerPage,
        page: currentPage
      });
      
      setTransactions(response.transactions || []);
      setTotalCount(response.total_count || response.transactions?.length || 0);
    } catch (err) {
      console.error('Error fetching transaction history:', err);
      setError('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, currency = 'BTC1') => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00000000';
    
    // Always show 8 decimal places for BTC1
    return Math.abs(num).toFixed(8);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getAmountDisplay = (tx) => {
    let amount = 0;
    let isPositive = true;

    if (tx.type === 'swap') {
      // For swaps, show the BTC1 amount (to_amount if to_currency is BTC1, or from_amount if from_currency is BTC1)
      if (tx.to_currency === 'BTC1') {
        amount = parseFloat(tx.to_amount || 0);
        isPositive = true;
      } else if (tx.from_currency === 'BTC1') {
        amount = parseFloat(tx.from_amount || 0);
        isPositive = false;
      } else {
        amount = 0;
      }
    } else if (tx.type === 'deposit') {
      amount = parseFloat(tx.amount || 0);
      isPositive = true;
    } else if (tx.type === 'transaction') {
      amount = parseFloat(tx.amount || 0);
      isPositive = amount > 0;
    } else if (tx.type === 'fiat_request') {
      amount = parseFloat(tx.amount || 0);
      isPositive = true;
    }

    const formattedAmount = formatAmount(amount);
    const sign = isPositive ? '+' : '-';
    
    return { amount: formattedAmount, sign, isPositive };
  };

  // Pagination logic
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);
  // Table Header
  const renderTableHeader = useMemo(
    () => (
      <div className="grid grid-cols-3 gap-4 px-4 sm:px-6 py-3 sm:py-4 bg-[#FDD00F] rounded-full border !border-black mb-4">
        <div className="flex items-center">
          <span className="text-base sm:text-base font-bold !text-white uppercase tracking-wider">
            Transaction
          </span>
        </div>
        <div className="flex items-center justify-center">
          <span className="text-base sm:text-base font-bold !text-white uppercase tracking-wider">
            Time
          </span>
        </div>
        <div className="flex items-center justify-center">
          <span className="text-base sm:text-base font-bold !text-white uppercase tracking-wider">
            Amount
          </span>
        </div>
      </div>
    ),
    []
  );

  // Render Transaction Row
  const renderTransactionRow = (tx, index) => {
    const { amount, sign, isPositive } = getAmountDisplay(tx);
    
    return (
      <div
        key={tx.id || index}
        className="grid grid-cols-3 gap-4 px-4 sm:px-6 py-4 bg-[#182133] rounded-xl transition-all duration-300 hover:bg-[#1e2a42]"
      >
        {/* Transaction Column */}
        <div className="flex items-center">
          <span className="text-white font-medium text-base">BTC1</span>
        </div>

        {/* Time Column */}
        <div className="flex items-center justify-center">
          <span className="text-white text-sm">{formatDate(tx.created)}</span>
        </div>

        {/* Amount Column */}
        <div className="flex items-center justify-center">
          <span className={`text-base font-semibold ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
            {sign}{amount} BTC1
          </span>
        </div>
      </div>
    );
  };

  // Pagination Component
  const renderPagination = useMemo(() => {
    if (totalPages <= 0) return null;

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between mt-4 sm:mt-6 px-2 sm:px-4 py-3 rounded-xl gap-3 sm:gap-0">
        <div className="text-xs text-white font-medium text-center sm:text-left">
          Showing{' '}
          <span className="text-white font-bold">{startIndex + 1}</span> to{' '}
          <span className="text-white font-bold">{endIndex}</span> of{' '}
          <span className="text-white font-bold">{totalCount}</span> transactions
        </div>

        <div className="flex items-center space-x-1 sm:space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex items-center px-2 sm:px-3 py-1.5 text-xs font-semibold text-white bg-slate-700/60 border-2 border-slate-600/60 rounded-lg hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300"
          >
            <ChevronLeftIcon className="w-3 h-3 mr-1" />
            <span className="hidden sm:inline">Prev</span>
          </button>

          <div className="flex space-x-1 sm:space-x-2">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 sm:px-3 py-1.5 text-xs font-bold rounded-lg transition-all duration-300 ${
                    pageNum === currentPage
                      ? 'bg-[#0E1696] !text-white shadow-lg border-2 border-black'
                      : 'text-orange-300 bg-slate-700/60 border-2 border-slate-600/60 hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="flex items-center px-2 sm:px-3 py-1.5 text-xs font-semibold text-white bg-slate-700/60 border-2 border-slate-600/60 rounded-lg hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRightIcon className="w-3 h-3 ml-1" />
          </button>
        </div>
      </div>
    );
  }, [currentPage, totalPages, startIndex, endIndex, totalCount]);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 mt-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Transaction History</h2>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2A356D]"></div>
        </div>
        <div className="text-center text-white py-12">
          <p>Loading transactions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 mt-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Transaction History</h2>
        </div>
        <div className="text-center text-red-400 py-8">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-3" />
          <p className="text-lg mb-2">{error}</p>
          <button 
            onClick={fetchHistory}
            className="text-sm text-white hover:text-blue-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6 mt-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold  !text-white">Transaction History</h2>
          <button 
            onClick={fetchHistory}
          className="p-2 text-white hover:text-white transition-colors"
          title="Refresh"
          >
          <ArrowPathIcon className="h-5 w-5" />
          </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-600 pb-0">
        {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all relative ${
                filter === option.value
                ? 'text-white border-b-2 border-[#2A356D] -mb-[2px]'
                : 'text-white hover:text-white'
              }`}
            >
            {option.label}
            </button>
        ))}
      </div>

      {/* Transactions Table */}
      {transactions.length === 0 ? (
        <div className="text-center text-white py-12">
          <CurrencyDollarIcon className="h-16 w-16 mx-auto mb-4 text-white" />
          <p className="text-lg mb-2">No transactions found</p>
          <p className="text-sm">Your transaction history will appear here</p>
        </div>
      ) : (
        <>
          {/* Mobile: Scrollable container */}
          <div className="md:hidden btc1-history-mobile-scroll overflow-auto max-h-[60vh]">
            <div className="min-w-[600px]">
              {renderTableHeader}
              <div className="space-y-4">
                {transactions.map((tx, index) => renderTransactionRow(tx, index))}
                    </div>
                  </div>
                </div>
                
          {/* Medium and above: Normal layout */}
          <div className="hidden md:block">
            {renderTableHeader}
            <div className="space-y-4">
              {transactions.map((tx, index) => renderTransactionRow(tx, index))}
                </div>
              </div>
        </>
      )}

      {/* Pagination */}
      {transactions.length > 0 && renderPagination}
    </div>
  );
};

export default Btc1TransactionHistory;
